import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Inbox, ArrowRight, Clock, AlertTriangle, Receipt, HandCoins, Banknote, ClipboardCheck, Check, X, Loader2, Layers } from "lucide-react";
import { ChainButton } from "@/components/ApprovalChain";
import ChainActionDialog from "@/components/ChainActionDialog";

const actionLabel = { approve: "Approve", "pm-endorse": "Endorse", endorse: "Endorse", authorize: "Authorize", verify: "Verify", final: "Final approve", disburse: "Disburse", payment: "Post payment" };
const actionTone = {
  approve: "bg-emerald-100 text-emerald-800 border-emerald-200",
  final: "bg-emerald-100 text-emerald-800 border-emerald-200",
  authorize: "bg-sky-100 text-sky-800 border-sky-200",
  endorse: "bg-blue-100 text-blue-800 border-blue-200",
  "pm-endorse": "bg-blue-100 text-blue-800 border-blue-200",
  verify: "bg-violet-100 text-violet-800 border-violet-200",
  disburse: "bg-amber-100 text-amber-800 border-amber-200",
  payment: "bg-orange-100 text-orange-800 border-orange-200",
};
// SLA is now per-item from the backend (per-department config).
// Client keeps a fallback in case the API omits it.
const SLA_FALLBACK = { "pm-endorse": 3, endorse: 3, authorize: 3, verify: 3, final: 5, approve: 5, disburse: 7, payment: 3 };
const kindIcon = { reimbursement: Receipt, advance: HandCoins, expense: Receipt, payment: Banknote, working_draft: ClipboardCheck };
const kindLabel = { reimbursement: "RMB", advance: "Advance", expense: "Vendor bill", payment: "Payment", working_draft: "Working Budget" };
const CHAIN_KINDS = ["reimbursement", "advance", "expense"];

const endpointFor = (item, decision) => {
  if (CHAIN_KINDS.includes(item.kind)) {
    if (decision === "reject") return `/chain/${item.kind}/${item.id}/reject`;
    if (decision === "send-back") return `/chain/${item.kind}/${item.id}/send-back`;
    if (item.kind === "expense" && item.action === "verify") return null;
    return `/chain/${item.kind}/${item.id}/act`;
  }
  return item.action === "disburse" ? `/advances/${item.id}/disburse` : null;
};

const isBulkable = (item) => CHAIN_KINDS.includes(item.kind) && !item.on_behalf_required && !(item.kind === "expense" && item.action === "verify");

export default function ApprovalsInbox() {
  const { user } = useAuth();
  const [data, setData] = useState({ count: 0, items: [], by_kind: {} });
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("__ALL__");
  const [kindFilter, setKindFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [waiting, setWaiting] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checked, setChecked] = useState({});           // key -> bool  (key = kind:id)
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkComment, setBulkComment] = useState("");

  useEffect(() => { api.get("/projects").then(r => setProjects(r.data)); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedProject && selectedProject !== "__ALL__") params.set("project_id", selectedProject);
      if (kindFilter !== "all") params.set("kind", kindFilter);
      const r = await api.get(`/approvals/pending?${params}`);
      setData(r.data);
      api.get("/chain/waiting-requester").then(w => setWaiting(w.data || [])).catch(() => {});
      setChecked({});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [selectedProject, kindFilter]);

  const [onBehalf, setOnBehalf] = useState(false);
  const [target, setTarget] = useState(null);
  const openConfirm = (item, decision) => { setConfirm({ item, decision }); setComment(""); setOnBehalf(false); };
  const closeConfirm = () => { setConfirm(null); setComment(""); };

  const submitDecision = async () => {
    if (!confirm) return;
    const { item, decision } = confirm;
    const endpoint = endpointFor(item, decision);
    if (!endpoint) { toast.error("Action not supported inline"); return; }
    if (decision !== "approve" && !comment.trim()) { toast.error("A reason is required"); return; }
    if (decision === "approve" && confirm.item.on_behalf_required && !onBehalf) { toast.error("Tick the on-behalf confirmation first"); return; }
    setSubmitting(true);
    try {
      const body = decision === "approve"
        ? { step: item.action, comment: comment || "", on_behalf_confirmed: onBehalf }
        : { reason: comment };
      await api.post(endpoint, body);
      toast.success(`${item.reference} ${decision === "approve" ? "done: " + actionLabel[item.action] : decision === "reject" ? "rejected" : "sent back"}`);
      closeConfirm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally { setSubmitting(false); }
  };

  const filtered = data.items.filter(it => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (it.reference || "").toLowerCase().includes(q)
      || (it.requester_name || "").toLowerCase().includes(q)
      || (it.counterparty || "").toLowerCase().includes(q)
      || (it.project_name || "").toLowerCase().includes(q)
      || (it.inv_ref || "").toLowerCase().includes(q);
  });

  const bulkableFiltered = filtered.filter(isBulkable);
  const selectedItems = bulkableFiltered.filter(it => checked[`${it.kind}:${it.id}`]);
  const selectedCount = selectedItems.length;
  const selectedTotal = selectedItems.reduce((s, i) => s + (i.amount || 0), 0);
  const allBulkableChecked = bulkableFiltered.length > 0 && selectedCount === bulkableFiltered.length;

  const toggleRow = (item) => {
    const k = `${item.kind}:${item.id}`;
    setChecked(prev => ({ ...prev, [k]: !prev[k] }));
  };
  const toggleAll = () => {
    if (allBulkableChecked) { setChecked({}); return; }
    const next = {};
    bulkableFiltered.forEach(it => { next[`${it.kind}:${it.id}`] = true; });
    setChecked(next);
  };

  const submitBulk = async () => {
    if (selectedCount === 0) return;
    setSubmitting(true);
    try {
      const payload = {
        items: selectedItems.map(it => ({ kind: it.kind, id: it.id, action: it.action })),
        comment: bulkComment.trim() || "Bulk approved",
      };
      const r = await api.post("/approvals/bulk-approve", payload);
      toast.success(`Approved ${r.data.approved} of ${r.data.total}${r.data.failed.length ? ` — ${r.data.failed.length} failed` : ""}`);
      if (r.data.failed.length) {
        console.warn("Bulk approve failures:", r.data.failed);
      }
      setBulkOpen(false); setBulkComment(""); setChecked({});
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk approve failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Workflow · Approvals</div>
        <h1 className="font-display text-3xl font-extrabold mt-1 flex items-center gap-3">
          Pending on Me
          {data.count > 0 && <Badge className="bg-slate-900 text-white text-xs" data-testid="pending-count-badge">{data.count}</Badge>}
        </h1>
        <p className="text-sm text-slate-500 mt-1">Every request waiting for your action. Tick the box to bulk-approve routine batches — or decide row-by-row.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 mb-6">
        <Stat icon={Inbox} label="All pending" value={data.count} testId="stat-all" />
        <Stat icon={Receipt} label="Reimbursements" value={data.by_kind.reimbursement || 0} testId="stat-rmb" tone="violet" />
        <Stat icon={HandCoins} label="Advances" value={data.by_kind.advance || 0} testId="stat-adv" tone="amber" />
        <Stat icon={Banknote} label="Payments" value={data.by_kind.payment || 0} testId="stat-pay" tone="orange" />
        <Stat icon={ClipboardCheck} label="Working budgets" value={data.by_kind.working_draft || 0} testId="stat-wb" tone="emerald" />
      </div>

      {waiting.length > 0 && (
        <Card className="mb-4 border-rose-200" data-testid="waiting-requester-card">
          <CardContent className="p-3">
            <div className="text-xs uppercase tracking-wider text-rose-700 font-semibold mb-1">Waiting for requester ({waiting.length})</div>
            {waiting.map(w => <div key={w.id} className="text-xs flex gap-2 py-0.5" data-testid={`waiting-${w.reference}`}><span className="font-mono">{w.reference}</span><span>{w.requester_name}</span><span className="text-slate-500">₹{(w.amount || 0).toLocaleString()} · {w.reason}</span></div>)}
          </CardContent>
        </Card>
      )}
      <Card className="mb-4">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <Label>Project</Label>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger data-testid="approvals-project-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__ALL__">All projects</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Type</Label>
            <Tabs value={kindFilter} onValueChange={setKindFilter}>
              <TabsList data-testid="approvals-kind-tabs">
                <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
                <TabsTrigger value="reimbursement" data-testid="tab-reimbursement">RMB</TabsTrigger>
                <TabsTrigger value="advance" data-testid="tab-advance">Advance</TabsTrigger>
                <TabsTrigger value="payment" data-testid="tab-payment">Payment</TabsTrigger>
                <TabsTrigger value="working_draft" data-testid="tab-working-draft">Budget</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div>
            <Label>Search</Label>
            <Input placeholder="Ref / requester / vendor / INV number" value={query} onChange={e => setQuery(e.target.value)} data-testid="approvals-search" />
          </div>
        </CardContent>
      </Card>

      {selectedCount > 0 && (
        <div className="mb-3 flex items-center justify-between gap-3 bg-slate-900 text-white rounded-lg px-4 py-3" data-testid="bulk-action-bar">
          <div className="flex items-center gap-3">
            <Layers className="w-4 h-4" />
            <div className="text-sm">
              <b>{selectedCount}</b> selected · Total <b>₹ {selectedTotal.toLocaleString()}</b>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" className="text-white hover:bg-slate-800" onClick={() => setChecked({})}>Clear</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setBulkOpen(true)} data-testid="bulk-approve-btn">
              <Check className="w-4 h-4 mr-1" /> Bulk approve
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 bg-slate-50">
              <tr className="border-b">
                <th className="px-3 py-2 w-8">
                  <Checkbox checked={allBulkableChecked} onCheckedChange={toggleAll} disabled={bulkableFiltered.length === 0} aria-label="Select all bulkable rows" data-testid="select-all-checkbox" />
                </th>
                <th className="text-left px-3 py-2">Ref</th>
                <th className="text-left px-3 py-2">Kind</th>
                <th className="text-left px-3 py-2">Requester / Vendor</th>
                <th className="text-left px-3 py-2">Project</th>
                <th className="text-right px-3 py-2">Amount</th>
                <th className="text-right px-3 py-2">Age</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-right px-3 py-2 min-w-[220px]">Decide</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-center px-3 py-10 text-slate-500">Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center px-3 py-16 text-slate-500">
                  <Inbox className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <div className="font-medium text-slate-700">All caught up.</div>
                  <div className="text-xs mt-1">Nothing is waiting for {user?.name} right now.</div>
                </td></tr>
              )}
              {!loading && filtered.map(it => {
                const Icon = kindIcon[it.kind] || Inbox;
                const sla = it.sla_days ?? SLA_FALLBACK[it.action] ?? 7;
                const stale = it.age_days > sla;
                // Working-budget drafts have their own dedicated review dialog
                // reached via the deep-link — no inline Approve/Reject here.
                const canInline = CHAIN_KINDS.includes(it.kind) && ["endorse", "authorize", "verify", "final"].includes(it.action);
                const bulkable = isBulkable(it);
                const rowKey = `${it.kind}:${it.id}`;
                return (
                  <tr key={rowKey} className={`border-b border-slate-100 hover:bg-slate-50 ${stale ? "bg-red-50/40" : ""} ${checked[rowKey] ? "bg-emerald-50/40" : ""}`} data-testid={`approval-row-${it.reference}`}>
                    <td className="px-3 py-3">
                      {bulkable ? (
                        <Checkbox checked={!!checked[rowKey]} onCheckedChange={() => toggleRow(it)} aria-label={`Select ${it.reference}`} data-testid={`row-checkbox-${it.reference}`} />
                      ) : (
                        <span className="inline-block w-4 h-4" />
                      )}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs font-semibold">{it.reference}</td>
                    <td className="px-3 py-3"><Icon className="w-4 h-4 inline mr-1 text-slate-500" /><span className="text-xs uppercase tracking-wider text-slate-600">{kindLabel[it.kind] || it.kind}</span></td>
                    <td className="px-3 py-3">
                      <div className="font-medium">{it.counterparty || it.requester_name}</div>
                      {it.counterparty !== it.requester_name && <div className="text-xs text-slate-500">from {it.requester_name}</div>}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-600">{it.project_name || <span className="text-slate-400">—</span>}{it.routing_note && <div className="text-[10px] text-amber-700" data-testid={`routing-note-${it.id}`}>{it.routing_note}</div>}</td>
                    <td className="px-3 py-3 text-right font-mono font-bold text-emerald-700">₹ {(it.amount || 0).toLocaleString()}</td>
                    <td className="px-3 py-3 text-right">
                      <span className={`inline-flex items-center gap-1 text-xs font-mono ${stale ? "text-red-700 font-semibold" : "text-slate-600"}`} title={`SLA ${sla}d`}>
                        {stale && <AlertTriangle className="w-3 h-3" />}
                        <Clock className="w-3 h-3" /> {it.age_days}d
                        <span className="text-[9px] text-slate-400 ml-0.5">/{sla}d</span>
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <Badge variant="outline" className={`text-[10px] uppercase font-semibold border ${actionTone[it.action] || ""}`}>{actionLabel[it.action] || it.action}</Badge>
                      {it.combined_flag && <div className="text-[10px] text-rose-700 mt-1" data-testid={`combined-flag-${it.id}`}>Combined claims this month ≥ limit</div>}
                      {(CHAIN_KINDS.includes(it.kind) || it.kind === "payment") && <div className="mt-1"><ChainButton kind={it.kind === "payment" ? "reimbursement" : it.kind} id={it.id} testId={`chain-btn-${it.reference}`} /></div>}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        {canInline ? (
                          <>
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-2.5" onClick={() => setTarget({ kind: it.kind, id: it.id })} data-testid={`approve-btn-${it.reference}`}>
                              <Check className="w-3.5 h-3.5 mr-1" /> {actionLabel[it.action] || "Approve"}
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 px-2.5" onClick={() => setTarget({ kind: it.kind, id: it.id })} data-testid={`sendback-btn-${it.reference}`}>
                              Send back
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 px-2.5 border-red-200 text-red-700 hover:bg-red-50" onClick={() => setTarget({ kind: it.kind, id: it.id })} data-testid={`reject-btn-${it.reference}`}>
                              <X className="w-3.5 h-3.5 mr-1" /> Reject
                            </Button>
                          </>
                        ) : null}
                        <Link to={it.deep_link}><Button size="sm" variant="ghost" className="h-8 px-2" data-testid={`open-btn-${it.reference}`}>Open <ArrowRight className="w-3 h-3 ml-1" /></Button></Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {data.count > 0 && (
        <div className="mt-4 text-xs text-slate-500 flex items-center gap-4 flex-wrap">
          <span><span className="inline-block w-3 h-3 bg-red-50 border border-red-200 mr-1"></span> Aged past SLA</span>
          <span className="text-slate-400">·</span>
          <span>SLA is per-department — Admin can tune it at <b>Settings · SLA</b>.</span>
          <span className="text-slate-400">·</span>
          <span>Disbursements & payments need bank/UTR details — use <b>Open</b> for those.</span>
        </div>
      )}

      <ChainActionDialog target={target} onClose={() => setTarget(null)} onDone={load} />

      {/* Single-item confirm */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && closeConfirm()}>
        <DialogContent data-testid="approval-confirm-dialog">
          {confirm && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {confirm.decision === "approve" ? <Check className="w-5 h-5 text-emerald-600" /> : <X className="w-5 h-5 text-red-600" />}
                  {confirm.decision === "approve" ? actionLabel[confirm.item.action] : confirm.decision === "reject" ? "Reject" : "Send back"} {confirm.item.reference}
                </DialogTitle>
                <DialogDescription>
                  {confirm.item.kind === "advance" ? "Advance" : "Reimbursement"} of <b>₹ {(confirm.item.amount || 0).toLocaleString()}</b> from <b>{confirm.item.requester_name}</b>
                  {confirm.item.counterparty !== confirm.item.requester_name && <> for <b>{confirm.item.counterparty}</b></>}.
                </DialogDescription>
              </DialogHeader>
              <div className="py-2">
                <Label htmlFor="approval-comment" className="text-xs uppercase tracking-wider text-slate-500">
                  {confirm.decision === "approve" ? "Note (optional)" : "Reason (required)"}
                </Label>
                <Textarea id="approval-comment" value={comment} onChange={e => setComment(e.target.value)} rows={3}
                  placeholder={confirm.decision === "approve" ? "Optional note the requester will see" : "Explain why…"}
                  data-testid="approval-comment-input"
                />
                {confirm.decision === "approve" && confirm.item.on_behalf_required && (
                  <label className="flex items-center gap-2 mt-3 text-sm" data-testid="on-behalf-label">
                    <input type="checkbox" checked={onBehalf} onChange={e => setOnBehalf(e.target.checked)} data-testid="on-behalf-checkbox" />
                    Endorsed on behalf of PM – supporting documents checked
                  </label>
                )}
                {confirm.item.routing_note && <p className="text-xs text-amber-700 mt-2">{confirm.item.routing_note}</p>}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={closeConfirm} disabled={submitting}>Cancel</Button>
                <Button
                  onClick={submitDecision}
                  disabled={submitting}
                  className={confirm.decision === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
                  data-testid="approval-confirm-btn"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Working…</> : (confirm.decision === "approve" ? `Confirm ${actionLabel[confirm.item.action]}` : confirm.decision === "reject" ? "Confirm Reject" : "Confirm Send back")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk confirm */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="bulk-approve-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Layers className="w-5 h-5 text-emerald-600" /> Bulk approve {selectedCount} item{selectedCount !== 1 ? "s" : ""}</DialogTitle>
            <DialogDescription>
              Total value <b>₹ {selectedTotal.toLocaleString()}</b>. Each item advances to its next stage. Rejected & un-actionable rows are skipped and reported back.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-52 overflow-y-auto border rounded bg-slate-50 my-2">
            <table className="w-full text-xs">
              <tbody>
                {selectedItems.map(it => (
                  <tr key={`${it.kind}:${it.id}`} className="border-b border-slate-100 last:border-0">
                    <td className="px-3 py-1.5 font-mono">{it.reference}</td>
                    <td className="px-3 py-1.5 text-slate-600">{actionLabel[it.action]}</td>
                    <td className="px-3 py-1.5 text-slate-500">{it.counterparty || it.requester_name}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-emerald-700">₹ {(it.amount || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Note (optional)</Label>
            <Textarea rows={2} value={bulkComment} onChange={e => setBulkComment(e.target.value)}
              placeholder="Applied to every item in the batch"
              data-testid="bulk-approve-comment"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkOpen(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={submitBulk} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-approve-confirm">
              {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Working…</> : `Confirm bulk approve (${selectedCount})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, testId }) {
  const toneCls = tone === "violet" ? "text-violet-700" : tone === "amber" ? "text-amber-700" : tone === "orange" ? "text-orange-700" : "text-slate-900";
  return (
    <Card><CardContent className="p-4" data-testid={testId}>
      <div className="text-xs uppercase text-slate-500 flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      <div className={`font-display font-bold text-2xl mt-1 ${toneCls}`}>{value}</div>
    </CardContent></Card>
  );
}
